import Input from "../inputs/Input";
import { AiOutlineLoading } from "react-icons/ai";
import { Alert, Button } from "flowbite-react";
import useCEP from "../../hooks/useCEP";
import { Controller, useForm } from "react-hook-form";
import { USERS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useMemo, useState } from "react";
import CPFInput from "../inputs/CPFInput";
import PhoneInput from "../inputs/PhoneInput";
import { resetValuesForm } from "../../utils/utils";
import SelectInput from "../inputs/SelectInput";
import { HiInformationCircle } from "react-icons/hi";
import ImageInput from "../inputs/ImageInput";
import { useApplication } from "../../contexts/ApplicationContext";
import { FiPhone, FiMapPin, FiShield } from "react-icons/fi";

/**
 * UserForm — versão moderna, clara e responsiva
 * - Layout em cartões/seções com títulos
 * - Grid responsivo 1/2 colunas
 * - Ações destacadas no rodapé
 * - Estados de erro elegantes
 */
const UserForm = ({
  user,
  fetchUsers,
  onClose,
  isOpen,
  type = "CREATE",
  companyId = null,
}) => {
  const { userPermission } = useApplication();
  const isPowerUser = userPermission === "POWER_USER";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [avatarImg, setAvatarImg] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const canManageAccess = ["ADMIN", "POWER_USER"].includes(userPermission);
  const tabs = useMemo(() => {
    const base = [
      { id: "contact", label: "Contato", Icon: FiPhone },
      { id: "address", label: "Endereço", Icon: FiMapPin },
    ];
    if (canManageAccess) {
      base.push({ id: "access", label: "Acesso", Icon: FiShield });
    }
    return base;
  }, [canManageAccess]);
  const [activeTab, setActiveTab] = useState("contact");

  const {
    register,
    getValues,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      cpf: "",
      phoneNumber: "",
      email: "",
      name: "",
      password: "",
      monthlyRequestLimit: 300,
    },
  });

  // CEP hook
  const { setCep, address, loading: loadingCEP } = useCEP();
  const onClickSearchCEP = () => setCep(getValues("addressPostalCode"));

  // Preenche endereço quando CEP resolve
  useEffect(() => {
    setValue("address", address?.logradouro);
    setValue("addressNeighborhood", address?.bairro);
    setValue("addressCity", address?.localidade);
  }, [address, setValue]);

  // Modo edição x criação
  useEffect(() => {
    if (type === "EDIT" && user) {
      Object.keys(user).forEach((k) => setValue(k, user[k]));
      setPreviewUrl(user?.imageUrl || "");
      setAvatarImg(user?.avatar || "");
    } else if (type === "CREATE") {
      resetValuesForm(["cpf", "phoneNumber"], setValue, reset);
      setPreviewUrl("");
      setAvatarImg("");
    }
    setValue("password", "");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isOpen]);

  useEffect(() => {
    if (!tabs.find((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id || "contact");
    }
  }, [tabs, activeTab]);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      if (type === "CREATE") {
        data.companyId = companyId;
        await axios.post(USERS.GET_USER, data);
      } else {
        if (data.password === "") delete data.password;
        await axios.put(`${USERS.GET_USER}/${user.id}`, data);
      }
      onClose?.(false);
      fetchUsers?.();
      if (type === "CREATE")
        resetValuesForm(["cpf", "phoneNumber"], setValue, reset);
    } catch (err) {
      setError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mx-auto max-w-4xl">
      {/* Header */}
      <div className="mb-3 flex flex-wrap gap-2">
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveTab(id)}
            className={[
              "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition",
              activeTab === id
                ? "bg-slate-900 text-white shadow-sm"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200",
            ].join(" ")}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Contato & Documento */}
      <div
        className={[
          "mb-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm",
          activeTab === "contact" ? "" : "hidden",
        ].join(" ")}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Input
              type="text"
              label="Nome *"
              name="name"
              required={true}
              register={register}
              error={errors?.name && "Este campo é obrigatório"}
            />
          </div>
          <div>
            <Controller
              name="phoneNumber"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <PhoneInput
                  type="text"
                  label="Telefone *"
                  name={field.name}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  required
                  error={fieldState.error && "Este campo é obrigatório"}
                />
              )}
            />
          </div>
          <div>
            <Controller
              name="cpf"
              control={control}
              render={({ field }) => (
                <CPFInput
                  type="text"
                  label="CPF"
                  name={field.name}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </div>
          <div>
            <Input
              type="date"
              label="Data de nascimento"
              name="birthday"
              required={false}
              register={register}
            />
          </div>
        </div>
      </div>

      {/* Endereço */}
      <div
        className={[
          "mb-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm",
          activeTab === "address" ? "" : "hidden",
        ].join(" ")}
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              label="CEP"
              name="addressPostalCode"
              register={register}
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={onClickSearchCEP}
              className="w-full md:w-auto"
              isProcessing={loadingCEP}
              processingSpinner={
                <AiOutlineLoading className="h-5 w-5 animate-spin" />
              }
            >
              Buscar CEP
            </Button>
          </div>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Input
            type="text"
            label="Endereço"
            name="address"
            register={register}
          />
          <Input
            type="text"
            label="Número"
            name="addressNumber"
            register={register}
          />
          <Input
            type="text"
            label="Bairro"
            name="addressNeighborhood"
            register={register}
          />
          <Input
            type="text"
            label="Cidade"
            name="addressCity"
            register={register}
          />
        </div>
      </div>

      {/* Acesso & Permissões */}
      {canManageAccess && (
        <div
          className={[
            "mb-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm",
            activeTab === "access" ? "" : "hidden",
          ].join(" ")}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              type="email"
              label={`Email *`}
              name="email"
              required
              register={register}
            />
            <Input
              type="password"
              label={`Senha ${type === "CREATE" ? "*" : ""}`}
              name="password"
              required={type === "CREATE"}
              register={register}
            />
            <div>
              <SelectInput
                register={register}
                name="permissionId"
                label="Permissão de usuário"
              >
                {userPermission === "POWER_USER" && (
                  <option value="POWER_USER">Power User</option>
                )}
                <option value="ADMIN">Administrador</option>
                <option value="USER">Usuário</option>
                <option value="SELLER">Vendedor</option>
              </SelectInput>
            </div>
            <div>
              <Input
                type="number"
                label="Limite máx. consultas/mês"
                name="monthlyRequestLimit"
                register={register}
                min={1}
                max={isPowerUser ? undefined : 3000}
                registerOptions={{
                  min: { value: 1, message: "Mínimo permitido é 1" },
                  ...(isPowerUser
                    ? {}
                    : { max: { value: 3000, message: "Máximo permitido é 3000" } }),
                  onChange: (e) => {
                    const raw = e.target.value;
                    if (raw === "") return;
                    let num = Number(raw);
                    if (Number.isNaN(num)) return;
                    if (num < 1) num = 1;
                    if (!isPowerUser && num > 3000) num = 3000;
                    e.target.value = num;
                  },
                  setValueAs: (v) =>
                    v === "" || v === null || typeof v === "undefined"
                      ? undefined
                      : Number(v),
                }}
                error={errors?.monthlyRequestLimit?.message}
              />
            </div>
          </div>
        </div>
      )}

      {/* Rodapé de ações */}
      <div className="sticky bottom-2 z-10 mt-8 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white/80 p-3 backdrop-blur supports-[backdrop-filter]:bg-white/60 md:static md:border-0 md:bg-transparent md:p-0">
        {error && (
          <Alert
            color="failure"
            icon={HiInformationCircle}
            className="order-2 md:order-1"
          >
            {error?.config?.method !== "get"
              ? `Erro ao ${
                  error?.config?.method === "put" ? "atualizar" : "criar"
                } usuário`
              : "E-mail já cadastrado"}
          </Alert>
        )}
        <div className="order-1 flex flex-col-reverse gap-3 md:order-2 md:flex-row md:justify-end">
          <Button
            color="light"
            onClick={() => onClose?.(false)}
            type="button"
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="primary"
            isProcessing={isSubmitting}
            processingSpinner={
              <AiOutlineLoading className="h-6 w-6 animate-spin" />
            }
            disabled={isSubmitting}
          >
            {type === "CREATE" ? "Criar" : "Atualizar"} usuário
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UserForm;
